import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

const LANG_LABEL = { en: "English", pt: "Português", es: "Español", it: "Italiano" };

function DeleteConfirmModal({ account, onClose, onConfirm }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/api/accounts/${account.id}/`);
      onConfirm();
    } catch { setLoading(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm mx-4 animate-fade-in border border-border">
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">{t("account.removeTitle")}</h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t("common.confirmRemove")} <strong>{account.email}</strong>?
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete} loading={loading}>{t("common.remove")}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Accounts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const isSuperAdmin = !!user?.capabilities?.manage_system;

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get("/api/accounts/");
      setAccounts(res.data);
    } catch { setAccounts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user?.capabilities?.manage_users) fetchAccounts();
    else setLoading(false);
  }, [fetchAccounts, user]);

  if (user && !user.capabilities?.manage_users) {
    return <Navigate to="/liveness/admin/client-apps" replace />;
  }

  const roleLabel = (r) => t(`account.role.${r}`, r);

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("nav.accounts")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("account.listSubtitle")}</p>
        </div>
        <Button onClick={() => navigate("/liveness/admin/accounts/new")}>
          <Plus className="h-4 w-4 mr-2" />{t("account.new")}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("account.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("common.name")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("login.email")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("account.role")}</th>
                    {isSuperAdmin && <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("common.company")}</th>}
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("common.language")}</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => {
                    const name = [a.first_name, a.last_name].filter(Boolean).join(" ") || "—";
                    return (
                      <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 font-medium">{name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{a.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                            a.is_super
                              ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                              : a.role === "institution_admin"
                              ? "bg-sky-500/10 text-sky-400 border-sky-500/30"
                              : "bg-muted text-muted-foreground border-border"
                          }`}>
                            {a.is_super ? t("account.role.super") : roleLabel(a.role)}
                          </span>
                        </td>
                        {isSuperAdmin && <td className="px-4 py-3 text-muted-foreground">{a.company_name || "—"}</td>}
                        <td className="px-4 py-3 text-muted-foreground">{LANG_LABEL[a.language] || a.language}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => navigate(`/liveness/admin/accounts/${a.id}/edit`)}
                              className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title={t("common.edit")}>
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button onClick={() => setDeleting(a)}
                              className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-muted-foreground hover:text-red-600" title={t("common.remove")}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {deleting && (
        <DeleteConfirmModal account={deleting} onClose={() => setDeleting(null)}
          onConfirm={() => { setDeleting(null); fetchAccounts(); }} />
      )}
    </div>
  );
}
